<script>
import { mapActions } from "vuex";
import AppIcon from "@/features/general/Icons";

const TRANSACTION_CONFIG = {
  "EARN:AVAILABLE": {
    icon: "check",
    circleBg: "#dcf5dc",
    iconColor: "#2e7d32",
    badgeClass: "bg-success",
    badgeLabel: "Disponível",
  },
  "EARN:PENDING": {
    icon: "plus",
    circleBg: "#e8f5e9",
    iconColor: "#388e3c",
    badgeClass: "bg-warning text-dark",
    badgeLabel: "Pendente",
  },
  "EARN:EXPIRED": {
    icon: "ban",
    circleBg: "#f5f5f5",
    iconColor: "#9e9e9e",
    badgeClass: "bg-secondary",
    badgeLabel: "Expirado",
  },
  "EARN:CANCELED": {
    icon: "times",
    circleBg: "#f5f5f5",
    iconColor: "#9e9e9e",
    badgeClass: "bg-secondary",
    badgeLabel: "Cancelado",
  },
  "REDEEM:AVAILABLE": {
    icon: "shopping-cart",
    circleBg: "#e3f2fd",
    iconColor: "#1565c0",
    badgeClass: "bg-primary",
    badgeLabel: "Aplicado",
  },
  "REVERSAL:AVAILABLE": {
    icon: "undo",
    circleBg: "#e8f5e9",
    iconColor: "#2e7d32",
    badgeClass: "bg-success",
    badgeLabel: "Revertido",
  },
  "REVERSAL:CANCELED": {
    icon: "undo",
    circleBg: "#f5f5f5",
    iconColor: "#9e9e9e",
    badgeClass: "bg-secondary",
    badgeLabel: "Cancelado",
  },
  "REVERSAL:PENDING": {
    icon: "undo",
    circleBg: "#fff8e1",
    iconColor: "#f57f17",
    badgeClass: "bg-warning text-dark",
    badgeLabel: "Pendente",
  },
  "MANUAL:AVAILABLE": {
    icon: "gift",
    circleBg: "#f3e5f5",
    iconColor: "#7b1fa2",
    badgeClass: "bg-success",
    badgeLabel: "Disponível",
  },
  "MANUAL:PENDING": {
    icon: "clock",
    circleBg: "#fff8e1",
    iconColor: "#f57f17",
    badgeClass: "bg-warning text-dark",
    badgeLabel: "Em carência",
  },
  "MANUAL:EXPIRED": {
    icon: "ban",
    circleBg: "#f5f5f5",
    iconColor: "#9e9e9e",
    badgeClass: "bg-secondary",
    badgeLabel: "Expirado",
  },
  "MANUAL:CANCELED": {
    icon: "times",
    circleBg: "#f5f5f5",
    iconColor: "#9e9e9e",
    badgeClass: "bg-secondary",
    badgeLabel: "Cancelado",
  },
};

const FALLBACK_CONFIG = {
  icon: "circle",
  circleBg: "#f5f5f5",
  iconColor: "#9e9e9e",
  badgeClass: "bg-secondary",
  badgeLabel: "",
};

const PT_BR_MONTHS = [
  "JANEIRO",
  "FEVEREIRO",
  "MARÇO",
  "ABRIL",
  "MAIO",
  "JUNHO",
  "JULHO",
  "AGOSTO",
  "SETEMBRO",
  "OUTUBRO",
  "NOVEMBRO",
  "DEZEMBRO",
];

export default {
  name: "AppCashbackTransactionList",

  components: {
    AppIcon,
  },

  props: {
    transactions: {
      type: Array,
      required: true,
    },
    hasMore: {
      type: Boolean,
      default: false,
    },
    loadingMore: {
      type: Boolean,
      default: false,
    },
    totalCount: {
      type: Number,
      default: 0,
    },
  },

  methods: {
    ...mapActions("Cashback", ["fetchMoreTransactions"]),

    /**
     * Carrega mais transações via paginação por cursor.
     * @return {Promise<void>}
     */
    async loadMore() {
      await this.fetchMoreTransactions({ http: this.$http });
    },

    /**
     * Retorna configuração visual para o par type:status da transação.
     * @param {object} transaction
     * @return {object}
     */
    transactionConfig(transaction) {
      const isManualCredit = transaction.reference_type === "manual_credit";
      const key = isManualCredit
        ? `MANUAL:${transaction.status}`
        : `${transaction.type}:${transaction.status}`;

      return TRANSACTION_CONFIG[key] || FALLBACK_CONFIG;
    },

    /**
     * Retorna a descrição legível da transação com verbo contextual.
     * @param {object} transaction
     * @return {string}
     */
    transactionDescription(transaction) {
      if (transaction.reference_type === "manual_credit") {
        return "Crédito manual da loja";
      }

      const descriptions = {
        "EARN:AVAILABLE": `Cashback do pedido #${transaction.reference_id} liberado`,
        "EARN:PENDING": `Cashback do pedido #${transaction.reference_id} gerado`,
        "EARN:EXPIRED": `Cashback do pedido #${transaction.reference_id} expirado`,
        "EARN:CANCELED": `Cashback do pedido #${transaction.reference_id} cancelado`,
        "REVERSAL:AVAILABLE": `Cashback do pedido #${transaction.reference_id} revertido`,
        "REVERSAL:CANCELED": `Cashback do pedido #${transaction.reference_id} cancelado`,
        "REVERSAL:PENDING": `Cashback do pedido #${transaction.reference_id} revertido`,
      };

      const key = `${transaction.type}:${transaction.status}`;

      if (descriptions[key]) {
        return descriptions[key];
      }

      if (transaction.type === "REDEEM") {
        return `Crédito usado no pedido #${transaction.reference_id}`;
      }

      return `Cashback do pedido #${transaction.reference_id}`;
    },

    /**
     * Retorna o meta-texto contextual da segunda linha da transação.
     * @param {object} transaction
     * @return {string}
     */
    transactionMeta(transaction) {
      if (transaction.reference_type === "manual_credit") {
        const manualMetaMap = {
          PENDING: transaction.available_at
            ? `Em carência até ${this.formatDate(transaction.available_at)}`
            : "Em carência",
          AVAILABLE: "Adicionado pela loja",
          EXPIRED: "Venceu sem ser usado",
          CANCELED: "Crédito cancelado",
        };

        return manualMetaMap[transaction.status] ?? "Adicionado pela loja";
      }

      const key = `${transaction.type}:${transaction.status}`;

      const metaMap = {
        "EARN:AVAILABLE": "7 dias após a entrega",
        "EARN:PENDING": transaction.available_at
          ? `Liberação em ${this.formatDate(transaction.available_at)}`
          : "Aguardando liberação",
        "EARN:EXPIRED": "Venceu sem ser usado",
        "EARN:CANCELED": "Pedido cancelado",
        "REVERSAL:AVAILABLE": "Estorno do pedido",
        "REVERSAL:CANCELED": "Pedido cancelado",
        "REVERSAL:PENDING": "Estorno em processamento",
      };

      if (metaMap[key] !== undefined) {
        return metaMap[key];
      }

      if (transaction.type === "REDEEM") {
        return "Aplicado no checkout";
      }

      return "";
    },

    /**
     * Formata data ISO para exibição no padrão brasileiro.
     * @param {string} isoDate
     * @return {string}
     */
    formatDate(isoDate) {
      return new Date(isoDate).toLocaleDateString("pt-BR");
    },

    /**
     * Retorna classe de cor do valor da transação.
     * @param {object} transaction
     * @return {string}
     */
    amountClass(transaction) {
      const isManualCredit = transaction.reference_type === "manual_credit";
      const isExpiredOrCanceled =
        transaction.status === "EXPIRED" || transaction.status === "CANCELED";
      const isCreditLikeType =
        transaction.type === "EARN" ||
        transaction.type === "REVERSAL" ||
        isManualCredit;
      const isNeutral = isCreditLikeType && isExpiredOrCanceled;

      if (isNeutral) {
        return "app__cashback__transaction-list__item__amount--neutral";
      }

      const isDebit = transaction.amount_cents < 0;

      return isDebit ? "text-danger" : "text-success";
    },

    /**
     * Retorna o valor formatado com sinal para exibição.
     * O sinal segue amount_cents: REVERSAL pode ser crédito (estorno de resgate,
     * positivo) ou débito (cancelamento de cashback liberado, negativo).
     * @param {object} transaction
     * @return {string}
     */
    formatAmount(transaction) {
      const value = (Math.abs(transaction.amount_cents) / 100).toLocaleString(
        "pt-BR",
        {
          style: "currency",
          currency: "BRL",
        }
      );

      return transaction.amount_cents < 0 ? `– ${value}` : `+ ${value}`;
    },

    /**
     * Monta a chave de agrupamento mês/ano no formato "YYYY-MM".
     * @param {string} isoDate
     * @return {string}
     */
    monthKey(isoDate) {
      const date = new Date(isoDate);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");

      return `${year}-${month}`;
    },

    /**
     * Retorna rótulo do mês no padrão "MAIO · 2026".
     * @param {string} key chave no formato "YYYY-MM"
     * @return {string}
     */
    monthLabel(key) {
      const [year, month] = key.split("-");
      const monthName = PT_BR_MONTHS[parseInt(month, 10) - 1];

      return `${monthName} · ${year}`;
    },

    /**
     * Calcula o líquido do mês para um grupo de transações.
     * @param {Array} items transações do grupo
     * @return {number} valor líquido em centavos
     */
    monthNetCents(items) {
      return items.reduce((sum, tx) => {
        const isManualCreditAvailable =
          tx.reference_type === "manual_credit" && tx.status === "AVAILABLE";
        const isEarnAvailable = tx.type === "EARN" && tx.status === "AVAILABLE";
        const isReversalAvailable =
          tx.type === "REVERSAL" && tx.status === "AVAILABLE";
        const isRedeem = tx.type === "REDEEM";

        if (
          isManualCreditAvailable ||
          isEarnAvailable ||
          isReversalAvailable ||
          isRedeem
        ) {
          return sum + tx.amount_cents;
        }

        return sum;
      }, 0);
    },

    /**
     * Formata o líquido do mês para exibição com sinal.
     * @param {number} cents valor em centavos
     * @return {string}
     */
    formatMonthNet(cents) {
      const value = (Math.abs(cents) / 100).toLocaleString("pt-BR", {
        style: "currency",
        currency: "BRL",
      });

      return cents >= 0 ? `+${value}` : `–${value}`;
    },
  },

  computed: {
    /**
     * Calcula quantas transações ainda não foram carregadas.
     * @return {number}
     */
    remaining() {
      return Math.max(0, this.totalCount - this.transactions.length);
    },

    /**
     * Agrupa transações por mês/ano com líquido calculado para exibição em seções.
     * @return {Array<{key: string, label: string, netCents: number, items: Array}>}
     */
    groupedTransactions() {
      const groups = {};
      const order = [];

      this.transactions.forEach((transaction) => {
        const key = this.monthKey(transaction.created_at);

        if (!groups[key]) {
          groups[key] = [];
          order.push(key);
        }

        groups[key].push(transaction);
      });

      return order.map((key) => ({
        key,
        label: this.monthLabel(key),
        netCents: this.monthNetCents(groups[key]),
        items: groups[key],
      }));
    },
  },
};
</script>

<template>
  <div class="app__cashback__transaction-list">
    <header class="app__cashback__transaction-list__header">
      <h5 class="app__cashback__transaction-list__title">
        Histórico de créditos
      </h5>
    </header>

    <div
      v-for="group in groupedTransactions"
      :key="group.key"
      class="app__cashback__transaction-list__group"
    >
      <div class="app__cashback__transaction-list__month-header">
        <span class="app__cashback__transaction-list__month-label">
          {{ group.label }}
        </span>
        <span
          v-if="group.netCents !== 0"
          class="app__cashback__transaction-list__month-net"
          :class="group.netCents > 0 ? 'text-success' : 'text-danger'"
        >
          Líquido do mês {{ formatMonthNet(group.netCents) }}
        </span>
      </div>

      <div
        v-for="transaction in group.items"
        :key="transaction.id"
        class="app__cashback__transaction-list__item"
      >
        <div
          class="app__cashback__transaction-list__item__circle"
          :style="{
            backgroundColor: transactionConfig(transaction).circleBg,
            color: transactionConfig(transaction).iconColor,
          }"
        >
          <app-icon :name="transactionConfig(transaction).icon" prefix="fa" />
        </div>

        <div class="app__cashback__transaction-list__item__body">
          <p class="app__cashback__transaction-list__item__desc">
            {{ transactionDescription(transaction) }}
          </p>
          <p class="app__cashback__transaction-list__item__meta">
            <span>{{ formatDate(transaction.created_at) }}</span>
            <template v-if="transactionMeta(transaction)">
              <span
                class="app__cashback__transaction-list__item__meta-dot"
                aria-hidden="true"
              ></span>
              <span>{{ transactionMeta(transaction) }}</span>
            </template>
            <span
              class="badge"
              :class="transactionConfig(transaction).badgeClass"
            >
              {{ transactionConfig(transaction).badgeLabel }}
            </span>
          </p>
        </div>

        <span
          class="app__cashback__transaction-list__item__amount"
          :class="amountClass(transaction)"
        >
          {{ formatAmount(transaction) }}
        </span>
      </div>
    </div>

    <div class="app__cashback__transaction-list__more" v-if="hasMore">
      <button
        class="app__button btn btn-outline-secondary"
        :disabled="loadingMore"
        @click="loadMore"
      >
        <span v-if="loadingMore">Carregando...</span>
        <span v-else
          >Ver mais movimentações
          <span v-if="remaining > 0">({{ remaining }} restantes)</span>
        </span>
      </button>
    </div>
  </div>
</template>
